import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { useProductsRaw } from '../hooks/useProducts';
import { addProduct, updateProduct, deleteProduct, migrateProductsToFirestore } from '../services/firebaseService';
import styles from './AdminPanel.module.css';

const AVAILABLE_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { products, loading } = useProductsRaw();
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productsList, setProductsList] = useState(products);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    category: 'nitrilo',
    price: '',
    description: '',
    sizes: [],
    image: '',
    images: '',
    url: '#',
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    setProductsList(products);
  }, [products]);

  const handleLogout = async () => {
    const result = await logoutUser();
    if (result.success) {
      navigate('/login');
    }
  };

  /**
   * Recarga TODOS los productos desde el JSON original
   * Esta es la opción "nuclear" - borra todo y recarga limpio
   */
  const handleReloadFromJSON = async () => {
    if (!window.confirm('⚠️ ADVERTENCIA: Esto eliminará TODOS los cambios y recargará los productos desde el archivo original.\n\n¿Continuar?')) {
      return;
    }

    setIsProcessing(true);
    try {
      const result = await migrateProductsToFirestore();
      if (result.success) {
        setMessage(`✅ ¡Éxito! Se cargaron ${result.count} productos. La página se recargará...`);
        setMessageType('success');
        setTimeout(() => {
          window.location.reload();
        }, 2500);
      } else {
        setMessage(`❌ Error: ${result.error}`);
        setMessageType('error');
      }
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
      setMessageType('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setFormData({
      title: '',
      subtitle: '',
      category: 'nitrilo',
      price: '',
      description: '',
      sizes: [],
      image: '',
      images: '',
      url: '#',
    });
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      subtitle: product.subtitle || '',
      category: product.category,
      price: String(Math.round(product.price)),
      description: product.description,
      sizes: Array.isArray(product.sizes) ? product.sizes : [],
      image: product.image || '',
      images: Array.isArray(product.images) ? product.images.join('\n') : '',
      url: product.url || '#',
    });
    setShowModal(true);
  };

  const toggleSize = (size) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size]
    }));
  };

  const handleSave = async () => {
    try {
      if (!formData.title.trim()) {
        setMessage('❌ El nombre del producto es obligatorio');
        setMessageType('error');
        return;
      }

      const price = parseFloat(formData.price) || 0;
      if (price < 0) {
        setMessage('❌ El precio no puede ser negativo');
        setMessageType('error');
        return;
      }

      // Procesar imágenes del textarea
      const imagesArray = formData.images
        .split('\n')
        .map(img => img.trim())
        .filter(img => img.length > 0);

      const dataToSave = {
        title: formData.title,
        subtitle: formData.subtitle,
        category: formData.category,
        price: Math.round(price),
        description: formData.description,
        sizes: formData.sizes,
        image: formData.image || (editingProduct ? editingProduct.image : ''),
        images: imagesArray.length > 0 ? imagesArray : (editingProduct ? editingProduct.images : []),
        url: formData.url || '#',
      };

      if (editingProduct) {
        const result = await updateProduct(editingProduct.firestoreId, dataToSave);
        
        if (result.success) {
          setMessage('✅ Producto actualizado');
          setMessageType('success');
          setShowModal(false);
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          setMessage(`❌ Error: ${result.error}`);
          setMessageType('error');
        }
      } else {
        // Agregar nuevo producto
        const result = await addProduct(dataToSave);
        
        if (result.success) {
          setMessage('✅ Producto agregado');
          setMessageType('success');
          setShowModal(false);
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          setMessage(`❌ Error: ${result.error}`);
          setMessageType('error');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage(`❌ Error: ${error.message}`);
      setMessageType('error');
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este producto?')) {
      return;
    }

    try {
      const result = await deleteProduct(product.firestoreId);
      if (result.success) {
        setMessage('✅ Producto eliminado');
        setMessageType('success');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setMessage(`❌ Error: ${result.error}`);
        setMessageType('error');
      }
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
      setMessageType('error');
    }
  };

  return (
    <div className={styles.adminPanel}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h1>Panel de Administración - Mediglove</h1>
            <p className={styles.userInfo}>Bienvenido, {user?.email}</p>
          </div>
          <button onClick={handleLogout} className={styles.logoutButton}>
            Cerrar sesión
          </button>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>Gestión de Productos</h2>
            <div className={styles.headerActions}>
              <button 
                onClick={handleReloadFromJSON}
                className={styles.migrateButton}
                disabled={isProcessing}
                title="Recarga los productos desde el archivo JSON original"
                style={{ backgroundColor: '#FF6B6B', fontSize: '0.9rem' }}
              >
                {isProcessing ? '⏳ Recargando...' : '🔄 Recargar desde JSON'}
              </button>
              <button onClick={openAddModal} className={styles.addButton} disabled style={{ opacity: '0.5', cursor: 'not-allowed' }}>
                ➕ Agregar Producto
              </button>
            </div>
          </div>

          {message && (
            <div className={`${styles.message} ${styles[messageType]}`}>
              {message}
            </div>
          )}

          <div style={{ 
            backgroundColor: '#FFF3CD', 
            border: '1px solid #FFC107', 
            padding: '12px 16px', 
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '0.9rem',
            color: '#333'
          }}>
            <strong>ℹ️ Nota:</strong> Puedes agregar, editar y eliminar productos directamente desde aquí. 
            <br/>
            Para las imágenes, escribe las rutas relativas (ej: <code>./img/vinilo/01/01.png</code>) y asegúrate de que esos archivos existan en <code>/public/img/</code>. 
            Las imágenes de galería van una por línea.
          </div>

          {loading ? (
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '60px 20px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #8ec9c3',
                borderTop: '4px solid #c9298e',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          ) : productsList.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              backgroundColor: '#F8F9FA',
              borderRadius: '8px',
              color: '#666'
            }}>
              <p>No hay productos cargados</p>
              <button 
                onClick={handleReloadFromJSON}
                className={styles.migrateButton}
                style={{ backgroundColor: '#28A745', marginTop: '10px' }}
              >
                Cargar productos ahora
              </button>
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Categoría</th>
                    <th>Precio</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {productsList.map((product) => (
                    <tr key={product.id}>
                      <td>{product.title}</td>
                      <td>{product.category}</td>
                      <td>${Math.round(product.price)}</td>
                      <td className={styles.actions}>
                        <button
                          onClick={() => openEditModal(product)}
                          className={styles.editBtn}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => handleDelete(product)}
                          className={styles.deleteBtn}
                        >
                          🗑️ Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>{editingProduct ? 'Editar Producto' : 'Agregar Producto'}</h3>
              <button
                onClick={() => setShowModal(false)}
                className={styles.closeBtn}
              >
                ✕
              </button>
            </div>

            <div className={styles.formScroll}>
              <div className={styles.formGroup}>
                <label>Nombre del Producto *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Ej: Guantes Nitrilo Azul"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Subtítulo / Código</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) =>
                    setFormData({ ...formData, subtitle: e.target.value })
                  }
                  placeholder="Ej: Cod. 08069-72. Según talle."
                />
              </div>

              <div className={styles.formGroup}>
                <label>Categoría</label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                >
                  <option value="nitrilo">Nitrilo</option>
                  <option value="esteriles">Estériles</option>
                  <option value="domesticos">Domésticos</option>
                  <option value="pe">PE</option>
                  <option value="vinilo">Vinilo</option>
                  <option value="natural-latex">Natural Latex</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Precio</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: e.target.value })
                  }
                  placeholder="Ej: 7.55"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Descripción del producto"
                  rows="4"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Tallas Disponibles</label>
                <div className={styles.sizesGrid}>
                  {AVAILABLE_SIZES.map(size => (
                    <label key={size} className={styles.sizeCheckbox}>
                      <input
                        type="checkbox"
                        checked={formData.sizes.includes(size)}
                        onChange={() => toggleSize(size)}
                      />
                      <span>{size}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Imagen de portada */}
              <div className={styles.formGroup}>
                <label>Imagen de Portada (ruta relativa)</label>
                <input
                  type="text"
                  value={formData.image}
                  onChange={(e) =>
                    setFormData({ ...formData, image: e.target.value })
                  }
                  placeholder="Ej: ./img/vinilo/01/01.png"
                />
                {formData.image && (
                  <p style={{ fontSize: '12px', color: '#8ec9c3', marginTop: '8px' }}>
                    Preview: {formData.image}
                  </p>
                )}
              </div>

              {/* Imágenes de galería */}
              <div className={styles.formGroup}>
                <label>Imágenes de Galería (una por línea)</label>
                <textarea
                  value={formData.images}
                  onChange={(e) =>
                    setFormData({ ...formData, images: e.target.value })
                  }
                  placeholder="./img/vinilo/01/02.jpg&#10;./img/vinilo/01/03.jpg&#10;./img/vinilo/01/04.jpg"
                  rows="5"
                />
                {formData.images && (
                  <div style={{ marginTop: '8px' }}>
                    <p style={{ fontSize: '12px', color: '#8ec9c3', marginBottom: '5px' }}>
                      Imágenes a cargar: {formData.images.split('\n').filter(img => img.trim()).length}
                    </p>
                    {formData.images.split('\n').filter(img => img.trim()).map((img, idx) => (
                      <p key={idx} style={{ fontSize: '11px', color: '#a0a0a0', margin: '2px 0' }}>
                        {idx + 1}. {img.trim()}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.modalActions}>
              <button
                onClick={() => setShowModal(false)}
                className={styles.cancelBtn}
              >
                Cancelar
              </button>
              <button onClick={handleSave} className={styles.saveBtn}>
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
